import { clientLogging } from '@bfa/nextjs-common/services';
import { externalRedirect } from '@bfa/nextjs-common/utils';
import { MutableRefObject } from 'react';
import URI from 'urijs';

import { ABSOLUTE_URLS } from '../../../../constants';
import {
  GQLGwsErrorType,
  GQLGwsType,
  GQLProgram,
  Maybe,
} from '../../../../gql.generated';
import { logAnalyticsEvent } from '../../../../utils/analytics';
import { AnalyticsEventKey } from '../../../../utils/analytics/constants';
import { AnalyticsEvent } from '../../../../utils/types';

const ANNUAL_UPGRADE: AnalyticsEventKey = 'ANNUAL_UPGRADE';
export const ELIGIBLITY_ERROR_MESSAGE =
  '[GWS Eligibility] There was an error trying to execute the query.';
export const NON_ELIGIBLE =
  '[GWS Eligibility] REDEMPTION_CANCELED_BY_ELIGIBILITY';
export const VIEW_ANNUAL_POPUP = 'Viewed Annual Upgrade Interstitial';
export const ALERT_ERROR_MESSAGE =
  'The content is empty or there was an error trying to execute the query.';

export const getAnnualUpgradeEventData = (
  label: string,
  name: AnalyticsEventKey = ANNUAL_UPGRADE
): AnalyticsEvent => ({
  name,
  label,
});

/**
 * This method returns the corresponding GWS type only for the programs that are
 * enabled for GWS.
 */
export const getValidAnnualUpgradeGwsType = (
  program: GQLProgram | null
): GQLGwsType => {
  switch (program) {
    case GQLProgram.GLAMBAG:
      return GQLGwsType.GLAMBAG_ANNUAL_UPGRADE_GIFT;
    case GQLProgram.FULLSIZE:
      return GQLGwsType.BOXYCHARM_ANNUAL_UPGRADE_GIFT;
    default:
      return GQLGwsType.INVALID_TYPE;
  }
};

/**
 * This method checks if there is a reason to redirect. The order of the evaluation indicates
 * the priority of possible errors.
 * @param hasAlertQueryError The page content query failed
 * @param eligibilityError GWS eligibility error message
 * @returns a string with the reason for redirection. Empty string implies no need to readirect.
 */
const getRedirectReason = (
  hasAlertQueryError: boolean,
  eligibilityError?: Maybe<GQLGwsErrorType>
): string => {
  if (eligibilityError) {
    return `[GWS Eligibility] ${eligibilityError}`;
  } else if (hasAlertQueryError) {
    return ALERT_ERROR_MESSAGE;
  }
  return '';
};

/** This method handle logs logic:
 *  - If there is no reason to redirect (happy path) or the reason is the user
 *    is not eligible, a view page CS event is triggered to bypass UES eligibility.
 *  - For any other redirect reason, an error is logged giving detail of it.
 */
const handleLog = (
  trackSent: MutableRefObject<boolean>,
  redirectReason: string
) => {
  if (
    !trackSent.current &&
    (!redirectReason || NON_ELIGIBLE === redirectReason)
  ) {
    logAnalyticsEvent(getAnnualUpgradeEventData(VIEW_ANNUAL_POPUP));
    trackSent.current = true;
  } else {
    clientLogging.logError(Error(redirectReason));
  }
};

/** Redirects if there is a reason to redirect */
const handleRedirection = (redirectReason: string) => {
  if (redirectReason) externalRedirect(ABSOLUTE_URLS.GLAM_BAG);
};

export const handleLogAndRedirection = (
  hasAlertQueryError: boolean,
  trackSent: MutableRefObject<boolean>,
  eligibilityError?: Maybe<GQLGwsErrorType>
): void => {
  const redirectReason = getRedirectReason(
    hasAlertQueryError,
    eligibilityError
  );
  handleLog(trackSent, redirectReason);
  handleRedirection(redirectReason);
};

export const getUrlWithParams = (
  url: string,
  params: Record<string, any> | string
): string => (params ? URI(url).addSearch(params).toString() : url);
